// JSON fetch wrapper. Tracks the CSRF token via the X-CSRF-Token response
// header from prior safe requests (the __Host-csrf cookie is HttpOnly per
// the middleware comment in internal/csrf/middleware.go, so document.cookie
// can't read it). Falls back to a document.cookie read for tests that mint
// non-HttpOnly cookies. All API endpoints expect JSON; cursor params (if
// any) ride in the request body (POST-for-complex-read) per WAF Rule 1.

import { getAccessToken, refreshSelfSession, renewAccessToken, forceReauth } from './auth';
import { handleCompromiseSignal } from './compromise';

const CSRF_COOKIE = '__Host-csrf';
const CSRF_HEADER = 'X-CSRF-Token';
const COMPROMISE_HEADER = 'X-Compromise-Signal';

// The tenancy gate denies a request whose session role claim no longer matches the
// caller's freshly-written membership with this distinct 403 code (issue 190). It is
// recoverable — the membership exists, only the in-memory token's claim is stale — so
// api() re-mints once and retries (issue 191), distinct from a genuine NO_COMPANY_ACCESS.
const STALE_ROLE_CODE = 'SESSION_ROLE_STALE';

// Bounded stale-role recovery (issue 204). The re-mint mints a CORRECT claim — it
// reads the membership doc — so a deny that survives it is staleness on the
// AUTHORITY side (the server's membership cache, issue 202), which nothing the
// client does can clear: only time can. So recovery is a short bounded wait rather
// than an immediate logout. Re-mint once, then replay the request a few times over a
// few seconds; only if the window outlasts that does the session end.
//
// MAX_STALE_ATTEMPTS counts REPLAYS, so the worst case is 1 + 3 fetches. Exported so
// tests assert the schedule instead of hard-coding numbers that could drift from it.
export const MAX_STALE_ATTEMPTS = 3;
// The wait before each replay AFTER the first, which is immediate: the re-mint
// preceding it already cost a round-trip, and the common case — claim-side staleness —
// is fixed by that re-mint alone. These later waits exist for the authority-side case,
// and total a few seconds. Indexed by the failed attempt number minus one, so its
// length is MAX_STALE_ATTEMPTS - 1 by construction.
export const STALE_BACKOFF_MS = [1500, 3000];

// The re-mint's own request chain: switchCompany's POST /api/auth/company plus the
// GET /api/me and RBAC-context reads it triggers. All three are PRE-tenancy, so none
// can return a D3 stale deny — but they run while a re-mint is in flight, and a
// request that entered recovery would await the very promise it is part of.
// Excluding them by path makes that deadlock impossible WITHOUT also excluding the
// sibling page requests that must share the in-flight re-mint. The boolean latch
// this replaces excluded both, which is what blanked admin pages: one request
// recovered while its siblings threw raw 403s into page catches (issue 204).
const REMINT_PATHS = new Set(['/api/auth/company', '/api/me', '/api/rbac/context']);

// The single re-mint in flight, shared rather than merely flagged: concurrent
// stale-403 callers await THIS promise and then replay, instead of skipping recovery.
// AdminUsersPage.load() fires four requests in one Promise.all, so the parallel burst
// is the common case, not an edge.
let staleRemint: Promise<void> | null = null;

// Expired-access-token recovery (issue 712). A 401 on a request that CARRIED a
// bearer means the short-lived token outlived the page's last refresh — a long
// authoring session on an admin editor — not that the user is signed out: the
// HttpOnly refresh cookie is still there. One shared renewal (POST /api/auth/refresh
// + the active-company re-bind, exactly boot's silent-refresh path) and one replay,
// so the write lands and the author's unsaved edit survives. A 401 that survives the
// renewal, or a renewal the server refuses, is the definitive signal and ends the
// session BY NAME (forceReauth 'session-expired' → the login notice), never as a
// generic server error with the edit still on screen.
//
// Three guards keep this from touching anything it must not: a request that sent no
// bearer (the login form, the boot probes) is a genuine "not signed in" and surfaces
// as before; the renewal's own chain (/api/auth/*, /api/me, /api/rbac/context) is
// excluded by path so it can never await the promise it is part of; and the replay
// is flagged, so a second 401 falls through rather than renewing again.
let sessionRenew: Promise<void> | null = null;
const SESSION_EXPIRED_REASON = 'session-expired';

const isSessionChainPath = (path: string): boolean => path.startsWith('/api/auth/') || REMINT_PATHS.has(path);

let cachedCsrfToken: string | null = null;

// A 403 with this code is the CSRF middleware's denial (issue 408): the header
// we sent no longer matches the HttpOnly cookie — or we sent none. Recoverable
// without user action: one safe GET re-syncs the cache from the response-header
// echo, then the request is replayed exactly once.
const CSRF_INVALID_CODE = 'CSRF_INVALID';

// The single CSRF re-seed in flight, shared like staleRemint below: concurrent
// denied callers await one safe GET rather than each firing their own.
let csrfSeed: Promise<void> | null = null;

// seedCsrfToken re-syncs the token cache with the cookie the jar actually
// holds, via the response-header echo on one safe GET. /api/me echoes the
// token on 401 too, so this works logged out — the login form's case.
function seedCsrfToken(): Promise<void> {
  csrfSeed ??= api('/api/me')
    .catch(() => {})
    .finally(() => {
      csrfSeed = null;
    }) as Promise<void>;
  return csrfSeed;
}

// Test hook: the CSRF cache is module-level state invisible to suites; this
// resets it so the cold-cache and desync recovery paths (issue 408) are
// exercised deterministically regardless of what ran before.
export function resetCsrfStateForTests(): void {
  cachedCsrfToken = null;
  csrfSeed = null;
}

function csrfToken(): string {
  if (cachedCsrfToken) return cachedCsrfToken;
  // The boot-time seed (PR 471 review, finding 4): cmd/server/spa.go mints
  // the CSRF cookie on the document response and stamps that same token into
  // this meta tag, because gating the boot-time /api/me probe behind a
  // session signal (issue 444) means a fresh, never-signed-in browser may
  // never get a /api/ response at all before its first POST (magic-link,
  // recovery, invite-accept) — so there is no X-CSRF-Token echo yet to have
  // populated cachedCsrfToken above. Since issue 738 a signed-in reload is a
  // cold-cache case too: boot's first request is now POST /api/auth/refresh,
  // with no GET ahead of it to echo a token, so this seed is what that POST
  // carries. Read lazily, on first use, rather than at module load, and only
  // once — the result is cached exactly like an echo.
  const seeded = document.querySelector('meta[name="fexo-csrf-seed"]')?.getAttribute('content')?.trim();
  if (seeded) {
    cachedCsrfToken = seeded;
    return seeded;
  }
  // Fallback for environments where the cookie is reachable from JS
  // (older dev http config, tests). Returns empty when the cookie is
  // HttpOnly — that's the production case and the response-header
  // capture below is what we actually rely on.
  const m = document.cookie.match(new RegExp('(?:^|; )' + CSRF_COOKIE.replace(/\$/g, '\\$') + '=([^;]+)'));
  return m ? decodeURIComponent(m[1]) : '';
}

type ApiOpts = {
  method?: string;
  body?: unknown;
  idempotencyKey?: string;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  // Internal: which stale-role replay this is (absent = the original request). It
  // counts rather than flags so the recovery can express "replay 2 of 3" and stop
  // (issue 204); a boolean could only ever mean "one shot, then log out".
  _staleAttempt?: number;
  // Internal: this request is the one-shot replay after a CSRF_INVALID deny
  // (issue 408). A boolean, not a counter: the re-seed either synced the cache
  // with the cookie or nothing the client does will.
  _csrfRetried?: boolean;
  // Internal: this request is the one-shot replay after an expired-token 401 was
  // recovered by a session renewal (issue 712). A boolean like _csrfRetried: the
  // renewal either minted a token the server accepts or the session is over.
  _authRetried?: boolean;
};

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

// requestHeaders is the auth + CSRF wiring every request carries: the bearer
// token when we hold one (vfco-auth-package's Protected middleware reads the
// short-lived JWT from Authorization; its own public endpoints ignore it) and
// the CSRF token on every unsafe method. Shared by api() and apiFetch() so a
// raw-body request (multipart, an SSE stream) is authenticated exactly like a
// JSON one — never a second, drifting copy of the rules.
export function requestHeaders(method: string, accept: string): Record<string, string> {
  const headers: Record<string, string> = { Accept: accept };
  if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    const token = csrfToken();
    if (token) headers[CSRF_HEADER] = token;
  }
  const access = getAccessToken();
  if (access) headers['Authorization'] = 'Bearer ' + access;
  return headers;
}

// observeResponse applies the two cross-cutting response rules: the CSRF echo
// (the middleware returns the current token on every response so JS can submit
// it on the next unsafe method without reading the HttpOnly cookie) and the
// compromised-device signal, honoured from any endpoint, not just /api/me.
function observeResponse(res: Response): void {
  observeHeaders((name) => res.headers.get(name));
}

// observeHeaders is observeResponse over any transport's response headers.
// Exported so the XHR upload path (services/apiUpload, kept out of the eager
// bundle) applies the same two rules rather than a second copy of them.
export function observeHeaders(getHeader: (name: string) => string | null): void {
  const headerToken = getHeader(CSRF_HEADER);
  if (headerToken) cachedCsrfToken = headerToken;
  if (getHeader(COMPROMISE_HEADER) === 'true') {
    handleCompromiseSignal();
  }
}

export type ApiFetchOpts = {
  method?: string;
  // A raw body: FormData (the one multipart path, POST /api/ai/ingest) or a
  // pre-encoded string. Content-Type is left to the caller — the browser sets
  // the multipart boundary itself, and setting it by hand breaks that.
  body?: BodyInit;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

// apiFetch is the raw-response sibling of api(): the same auth + CSRF wiring
// and the same response observation, but the caller owns the body encoding
// and reads the Response itself. It exists for the two shapes api() cannot
// carry — a multipart upload and a Server-Sent Events stream read through
// fetch + ReadableStream (FE-033) — and deliberately has none of api()'s
// stale-role or CSRF replay logic: a stream cannot be replayed once it has
// started, and an upload replay would re-send the file.
export async function apiFetch(path: string, opts: ApiFetchOpts = {}): Promise<Response> {
  const method = opts.method ?? 'GET';
  const headers = requestHeaders(method, opts.headers?.Accept ?? 'application/json');
  if (opts.headers) {
    for (const [k, v] of Object.entries(opts.headers)) headers[k] = v;
  }
  const res = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    body: opts.body,
    signal: opts.signal,
  });
  observeResponse(res);
  return res;
}

export async function api<T = unknown>(path: string, opts: ApiOpts = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers = requestHeaders(method, 'application/json');
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (opts.idempotencyKey) {
    headers['Idempotency-Key'] = opts.idempotencyKey;
  }
  // Caller-supplied headers last so they can set request-specific values
  // (e.g. X-Device-Class on refresh) without overriding the auth/CSRF wiring.
  if (opts.headers) {
    for (const [k, v] of Object.entries(opts.headers)) headers[k] = v;
  }
  const res = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });
  // CSRF echo capture + the compromised-device signal (the 401 still throws
  // below for the caller).
  observeResponse(res);
  const text = await res.text();
  const json = text ? safeJSON(text) : null;
  if (!res.ok) {
    // CSRF desync recovery (issue 408): the token cache no longer matches the
    // HttpOnly cookie (or was empty and no header went out). One safe GET
    // re-syncs the cache from the header echo, then this exact request replays
    // once. Bounded by the flag: a replay that is denied again surfaces as a
    // normal ApiError. The seed itself is a safe GET, so it can never recurse
    // into this branch.
    if (res.status === 403 && errorCodeOf(json) === CSRF_INVALID_CODE && !opts._csrfRetried) {
      cachedCsrfToken = null;
      await seedCsrfToken();
      return api<T>(path, { ...opts, _csrfRetried: true });
    }
    // Expired-token recovery (issue 712): see sessionRenew above. Only a request
    // that carried a bearer qualifies — headers['Authorization'] is what actually
    // went out, so a token installed by a concurrent renewal between this call's
    // send and its 401 cannot be mistaken for "we were signed in".
    if (res.status === 401 && headers['Authorization'] && !isSessionChainPath(path)) {
      if (!opts._authRetried) {
        sessionRenew ??= renewAccessToken().finally(() => {
          sessionRenew = null;
        });
        const renewed = await sessionRenew.then(
          () => true,
          () => false,
        );
        if (renewed) return api<T>(path, { ...opts, _authRetried: true });
      }
      // A refused renewal, or a 401 that survived one: the session is over. The
      // ApiError still throws below so the caller's catch runs; the navigation
      // forceReauth starts lands the named notice.
      void forceReauth(SESSION_EXPIRED_REASON);
    }
    // Bounded stale-role recovery (issues 191 + 204): a 403 SESSION_ROLE_STALE means
    // the caller's membership changed (their own save, or another actor's) and the
    // session's role claim no longer matches it — but the membership still exists, so
    // this is recoverable. Re-mint for the active company, then replay this exact
    // request, backing off between replays. Each replay re-reads the fresh token from
    // the auth module (getAccessToken above), never the stale one this call captured.
    //
    // Why a WAIT and not just a retry: the re-mint mints a correct claim, so a deny
    // that survives it is staleness on the server's side of the gate (issue 202's
    // membership cache) — the client cannot fix that, it can only outlast it. Escalating
    // after one failed replay, as this did before, turned a transient window into a
    // logout. This lives at the shared fetch choke point so every admin surface
    // recovers, not just Members.
    const attempt = opts._staleAttempt ?? 0;
    if (
      res.status === 403 &&
      errorCodeOf(json) === STALE_ROLE_CODE &&
      attempt < MAX_STALE_ATTEMPTS &&
      !REMINT_PATHS.has(path)
    ) {
      // Re-mint only on first detection. Repeating it would cost three requests per
      // replay to mint the same already-correct claim.
      if (attempt === 0) {
        // Concurrent callers share one re-mint: whoever arrives first starts it, the
        // rest await the same promise. A failed re-mint is not fatal here — the replay
        // surfaces the authoritative deny (NO_COMPANY_ACCESS, or a still-stale 403 that
        // the remaining attempts will bound).
        staleRemint ??= refreshSelfSession()
          .catch(() => {})
          .finally(() => {
            staleRemint = null;
          });
        await staleRemint;
      } else {
        await staleBackoff(STALE_BACKOFF_MS[attempt - 1] ?? 0, opts.signal);
      }
      return api<T>(path, { ...opts, _staleAttempt: attempt + 1 });
    }
    // The bounded recovery is exhausted: the stale window outlasted every replay (or
    // the re-mint had no company to mint against — the unbound-session gap, issue 198).
    // End the session outright (issue 200, superseding the in-place reload): forceReauth
    // logs out and redirects to /login, so the next sign-in mints a brand-new session
    // carrying the fresh role. forceReauth itself is loop-bounded across the navigation
    // (issue 204) — a fresh session that re-trips the gate inside the same window shows
    // the recovery status instead of bouncing again. Only SESSION_ROLE_STALE reaches
    // here: a genuine NO_COMPANY_ACCESS never re-mints, never replays, and so never logs
    // out (foreign-tenant guarantee intact, hard rule #2).
    if (
      attempt >= MAX_STALE_ATTEMPTS &&
      res.status === 403 &&
      errorCodeOf(json) === STALE_ROLE_CODE
    ) {
      void forceReauth('role-changed');
    }
    throw new ApiError(res.status, json, `api ${method} ${path}: ${res.status}`);
  }
  return json as T;
}

// staleBackoff waits ms before the next stale-role replay, resolving early — and
// clearing its timer — if the caller aborts. Pages tear down with an AbortController
// (and a destroyed guard), so an unmounted page must never leave a pending replay
// behind; the replay's own fetch then rejects with AbortError, which page catches
// already ignore.
function staleBackoff(ms: number, signal?: AbortSignal): Promise<void> {
  if (ms <= 0 || signal?.aborted) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const done = (): void => {
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', done);
      resolve();
    };
    const timer = window.setTimeout(done, ms);
    signal?.addEventListener('abort', done, { once: true });
  });
}

// errorCodeOf reads the machine `code` off a parsed error body ({ error, code }),
// or '' when absent — the shared envelope every Fexo rejection returns.
function errorCodeOf(body: unknown): string {
  if (body && typeof body === 'object' && 'code' in body) {
    const code = (body as { code?: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return '';
}

function safeJSON(t: string): unknown {
  try {
    return JSON.parse(t);
  } catch {
    return t;
  }
}
